import * as React from 'react'
import { useReplyToReview, useReportReview } from '@/hooks/useVendorReviews'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { FormError } from '@/components/ui/form-error'
import { reportVendorReviewInputSchema, writeVendorReviewReplyInputSchema } from '@lib/input/vendor-review.input'
import { REVIEW_REPLY_MAX, REVIEW_REPORT_MAX } from '@lib/constants/vendor-reviews'
import type { OwnVendorReviewDto } from '@lib/dto/vendor-review.dto'
import type { VendorReviewReplyStatus } from '@lib/enums/vendor-review-reply-status.enum'

/** Where the company's reply stands, as its people read it. */
const REPLY_STATUS: Record<VendorReviewReplyStatus, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Waiting for approval', tone: 'warning' },
  approved: { label: 'Published', tone: 'success' },
  declined: { label: 'Not published', tone: 'danger' },
}

interface ReplyDialogProps {
  review: OwnVendorReviewDto
  companyName: string
  onClose: () => void
}

/**
 * Writing the company's one reply, or changing one not yet published. It sends
 * the reply as it was when the dialog opened, never a later one: if a colleague
 * replies or changes it meanwhile (the list refreshes after a refused send),
 * the dialog says so rather than sending over what its writer never read.
 */
function ReplyDialog({ review, companyName, onClose }: ReplyDialogProps) {
  const reply = useReplyToReview()
  const [opened] = React.useState(review.myReply)
  const [body, setBody] = React.useState(opened?.body ?? '')
  const [error, setError] = React.useState<string | undefined>(undefined)
  const changed = (opened?.sentAt ?? null) !== (review.myReply?.sentAt ?? null)

  function submit(e: React.FormEvent): void {
    e.preventDefault()
    if (changed) return
    const parsed = writeVendorReviewReplyInputSchema.safeParse({ body })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message)
      return
    }
    setError(undefined)
    // A change names the version it changes, so one sent again meanwhile isn't overwritten.
    const input = opened === null ? parsed.data : { ...parsed.data, sentAt: opened.sentAt }
    reply.mutate({ reviewId: review.id, input }, { onSuccess: onClose })
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Reply as {companyName}</DialogTitle>
          <DialogDescription>It shows under the review, on your profile and your page.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit} noValidate>
          <Field label="Your reply" htmlFor="review-reply" error={error} hint={`${String(body.length)}/${String(REVIEW_REPLY_MAX)}`}>
            <Textarea id="review-reply" rows={5} maxLength={REVIEW_REPLY_MAX} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <p className="m-0 text-xs text-ink-muted">
            One reply per review. FLOH Venues checks it before it&rsquo;s published, and once it is, it can&rsquo;t be changed.
          </p>
          {changed ? (
            <FormError>
              Someone at your company {opened === null ? 'replied' : 'changed this reply'} since you opened this. Close this and read it before
              sending yours.
            </FormError>
          ) : null}
          {reply.isError && !changed ? <FormError>{requestErrorMessage(reply.error)}</FormError> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={reply.isPending || changed}>
              {reply.isPending ? 'Sending…' : 'Send reply'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface ReportDialogProps {
  review: OwnVendorReviewDto
  onClose: () => void
}

/** Asking FLOH Venues to look at a review, once. */
function ReportDialog({ review, onClose }: ReportDialogProps) {
  const report = useReportReview()
  const [reason, setReason] = React.useState('')
  const [error, setError] = React.useState<string | undefined>(undefined)

  function submit(e: React.FormEvent): void {
    e.preventDefault()
    const parsed = reportVendorReviewInputSchema.safeParse({ reason })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message)
      return
    }
    setError(undefined)
    report.mutate({ reviewId: review.id, input: parsed.data }, { onSuccess: onClose })
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Report this review</DialogTitle>
          <DialogDescription>FLOH Venues will keep it up or take it down. You can report a review once.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit} noValidate>
          <Field label="Why should FLOH Venues look at it?" htmlFor="review-report" error={error} hint={`${String(reason.length)}/${String(REVIEW_REPORT_MAX)}`}>
            <Textarea id="review-report" rows={4} maxLength={REVIEW_REPORT_MAX} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {report.isError ? <FormError>{requestErrorMessage(report.error)}</FormError> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={report.isPending}>
              {report.isPending ? 'Sending…' : 'Report review'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface Props {
  review: OwnVendorReviewDto
  companyName: string
  /** The reader is one of the company's owners, who reply and report. */
  canAnswer: boolean
}

/**
 * The company's answers to one review of it (docs/vendor-growth.spec.md,
 * "Reviews"): its one reply, wherever that stands, and its one report. Only
 * owners write either; everyone at the company sees where they stand.
 */
export function ReviewAnswers({ review, companyName, canAnswer }: Props) {
  const [open, setOpen] = React.useState<'reply' | 'report' | null>(null)
  const reply = review.myReply
  const changeable = canAnswer && reply !== null && reply.status !== 'approved'

  function reportLine() {
    if (review.report === null) return null
    return (
      <p className="m-0 text-xs text-ink-muted">
        {review.report.kept ? 'Reported. FLOH Venues kept it.' : 'Reported. FLOH Venues will look at it.'}
      </p>
    )
  }

  return (
    <div className="space-y-2 pt-1">
      {reply === null ? null : (
        <div className="ml-3 space-y-1 border-l-2 border-line pl-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-ink-muted">Your reply</span>
            <Badge tone={REPLY_STATUS[reply.status].tone}>{REPLY_STATUS[reply.status].label}</Badge>
          </div>
          <p className="m-0 whitespace-pre-line text-sm text-ink">{reply.body}</p>
          {reply.status === 'declined' && reply.declineReason !== null ? (
            <p className="m-0 text-xs text-ink-muted">&ldquo;{reply.declineReason}&rdquo;</p>
          ) : null}
        </div>
      )}
      {reportLine()}
      {canAnswer ? (
        <div className="flex flex-wrap gap-2">
          {reply === null ? (
            <Button size="sm" variant="outline" onClick={() => setOpen('reply')}>
              Reply
            </Button>
          ) : null}
          {changeable ? (
            <Button size="sm" variant="outline" onClick={() => setOpen('reply')}>
              Change reply
            </Button>
          ) : null}
          {review.report === null ? (
            <Button size="sm" variant="ghost" onClick={() => setOpen('report')}>
              Report
            </Button>
          ) : null}
        </div>
      ) : null}
      {open === 'reply' ? <ReplyDialog review={review} companyName={companyName} onClose={() => setOpen(null)} /> : null}
      {open === 'report' ? <ReportDialog review={review} onClose={() => setOpen(null)} /> : null}
    </div>
  )
}
